// Listes de souhaits - Espace Client
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Plus, Trash2, ShoppingCart, Share2, Eye, MoreVertical,
  Package, X, Edit, Check, List
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Carte produit favori
function FavoriteItem({ item, onRemove, onAddToCart }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-xl border border-gray-200 p-3 flex gap-3"
    >
      <div className="w-20 h-20 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <Package size={24} className="text-gray-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{item.title}</h3>
        <p className="text-lg font-bold text-orange-600 mt-1">{item.price.toFixed(2)} €</p>
        <div className="flex items-center gap-2 mt-2">
          <Button
            size="sm"
            onClick={() => onAddToCart(item)}
            className="bg-orange-500 hover:bg-orange-600 rounded-lg text-xs px-3 h-8"
          >
            <ShoppingCart size={12} className="mr-1" /> Ajouter
          </Button>
          <button
            onClick={() => onRemove(item)}
            className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Carte liste
function ListCard({ list, onSelect, isSelected }) {
  return (
    <motion.button
      whileHover={{ y: -2 }}
      onClick={() => onSelect(list)}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        isSelected 
          ? 'border-orange-500 bg-orange-50' 
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-900">{list.name}</h3>
        <span className="text-sm text-gray-500">{list.items.length} articles</span>
      </div>
      <p className="text-sm text-gray-500 line-clamp-1">{list.description || 'Aucune description'}</p>
      {list.items.length > 0 && (
        <div className="flex -space-x-2 mt-3">
          {list.items.slice(0, 4).map((item, idx) => (
            <div
              key={idx}
              className="w-8 h-8 rounded-lg bg-gray-100 border-2 border-white flex items-center justify-center overflow-hidden"
            >
              {item.image ? (
                <img src={item.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <Package size={12} className="text-gray-400" />
              )}
            </div>
          ))}
          {list.items.length > 4 && (
            <div className="w-8 h-8 rounded-lg bg-gray-900 border-2 border-white flex items-center justify-center">
              <span className="text-xs text-white font-medium">+{list.items.length - 4}</span>
            </div>
          )}
        </div>
      )}
    </motion.button>
  );
}

// Modal nouvelle liste
function NewListModal({ isOpen, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    onCreate({ name, description });
    setName('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Nouvelle liste</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la liste</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Outils pour la rénovation"
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Une courte description"
              className="rounded-xl"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
            Annuler
          </Button>
          <Button onClick={handleCreate} className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl">
            Créer
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ListesPage() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewListModal, setShowNewListModal] = useState(false);

  useEffect(() => {
    fetchLists();
  }, []);

  useEffect(() => {
    if (lists.length > 0 && !selectedList) {
      setSelectedList(lists[0]);
    }
  }, [lists]);

  const fetchLists = async () => {
    setLoading(true);
    try {
      // Données simulées
      setLists([
        {
          id: '1',
          name: 'Favoris',
          description: 'Mes produits préférés',
          isDefault: true,
          items: [
            { id: 'p1', title: 'Perceuse Bosch Professional GSB 18V', price: 149, image: null },
            { id: 'p2', title: 'Niveau laser Makita SK105DZ', price: 289, image: null },
            { id: 'p3', title: 'Coffret tournevis Facom', price: 89, image: null },
          ]
        },
        {
          id: '2',
          name: 'Rénovation cuisine',
          description: 'Outils pour refaire la cuisine',
          items: [
            { id: 'p4', title: 'Carrelette manuelle 600mm', price: 79, image: null },
            { id: 'p5', title: 'Colle carrelage C2 25kg', price: 24, image: null },
          ]
        },
        {
          id: '3',
          name: 'Électricité',
          description: 'Matériel électrique',
          items: []
        },
      ]);
    } catch (e) {
      console.error('Error fetching lists:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = (data) => {
    const newList = {
      id: String(lists.length + 1),
      ...data,
      items: []
    };
    setLists([...lists, newList]);
    setSelectedList(newList);
    toast.success('Liste créée');
  };

  const handleRemoveItem = (item) => {
    if (!selectedList) return;
    
    const updatedList = {
      ...selectedList,
      items: selectedList.items.filter(i => i.id !== item.id)
    };
    setSelectedList(updatedList);
    setLists(lists.map(l => l.id === selectedList.id ? updatedList : l));
    toast.success('Article retiré');
  };

  const handleAddToCart = (item) => {
    toast.success(`${item.title} ajouté au panier`);
  };

  const handleDeleteList = () => {
    if (!selectedList || selectedList.isDefault) {
      toast.error('Impossible de supprimer cette liste');
      return;
    }
    setLists(lists.filter(l => l.id !== selectedList.id));
    setSelectedList(lists.find(l => l.isDefault) || lists[0]);
    toast.success('Liste supprimée');
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="listes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Mes Listes</h1>
          <p className="text-gray-500">Organise tes produits favoris</p>
        </div>
        <Button
          onClick={() => setShowNewListModal(true)}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Nouvelle liste
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Listes */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wider mb-3">
              Tes listes ({lists.length})
            </h2>
            {lists.map((list) => (
              <ListCard
                key={list.id}
                list={list}
                isSelected={selectedList?.id === list.id}
                onSelect={setSelectedList}
              />
            ))}
          </div>

          {/* Contenu de la liste */}
          <div className="lg:col-span-2">
            {selectedList ? (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                {/* Header */}
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-bold text-lg text-gray-900">{selectedList.name}</h2>
                      <p className="text-sm text-gray-500">{selectedList.items.length} article(s)</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="rounded-lg">
                        <Share2 size={14} className="mr-1" /> Partager
                      </Button>
                      {!selectedList.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteList}
                          className="rounded-lg text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="p-5">
                  {selectedList.items.length === 0 ? (
                    <div className="text-center py-12">
                      <Heart size={48} className="mx-auto text-gray-300 mb-4" />
                      <h3 className="font-semibold text-gray-900 mb-2">Liste vide</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Ajoutez des produits à cette liste depuis la marketplace
                      </p>
                      <Button
                        onClick={() => window.location.href = '/produits'}
                        variant="outline"
                        className="rounded-xl"
                      >
                        <Package size={16} className="mr-2" />
                        Parcourir les produits
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <AnimatePresence>
                        {selectedList.items.map((item) => (
                          <FavoriteItem
                            key={item.id}
                            item={item}
                            onRemove={handleRemoveItem}
                            onAddToCart={handleAddToCart}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {selectedList.items.length > 0 && (
                  <div className="p-5 border-t border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-600">Total de la liste</span>
                      <span className="text-xl font-bold text-gray-900">
                        {selectedList.items.reduce((sum, item) => sum + item.price, 0).toFixed(2)} €
                      </span>
                    </div>
                    <Button
                      onClick={() => {
                        selectedList.items.forEach(item => handleAddToCart(item));
                      }}
                      className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
                    >
                      <ShoppingCart size={18} className="mr-2" />
                      Tout ajouter au panier
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <List size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Sélectionnez une liste</h3>
                <p className="text-gray-500">Choisissez une liste pour voir son contenu</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New List Modal */}
      <NewListModal
        isOpen={showNewListModal}
        onClose={() => setShowNewListModal(false)}
        onCreate={handleCreateList}
      />
    </div>
  );
}
